import { Add } from "@mui/icons-material";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomDialog } from "../../common/components/CustomDialog";
import CustomLoader from "../../common/components/CustomLoader";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import CustomTable from "../../common/components/CustomTable";
import { getForm } from "../../common/config/forms";
import { usersHeadcells } from "../../common/config/headcells";
import { useAuth } from "../../common/hooks/useAuth";
import { useLoading } from "../../common/hooks/useLoading";
import { ICustomDialogProps } from "../../common/interfaces/dialog";
import { ICustomOption } from "../../common/interfaces/form";
import { ICustomPaginationProps } from "../../common/interfaces/pagination";
import { ACTIONS } from "../../common/interfaces/table";
import { IUser, IUserBody } from "../../common/interfaces/user";
import areaExpertiseService from "../../common/services/areaExpertiseService";
import rolesService from "../../common/services/rolesService";
import userService from "../../common/services/userService";

interface IUsersProps { }

const Users: FC<IUsersProps> = () => {

    // global
    const { permissionsState } = useAuth();
    const { loading } = useLoading();
    const { t } = useTranslation();
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));

    const [action, setAction] = useState<ACTIONS>();
    const [users, setUsers] = useState<(IUser & { areaOfExpertise: string, roleName: string, actions: { type: ACTIONS, message: string, disabled?: boolean }[] })[]>([]);
    const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);

    // form
    const addUserForm = getForm<IUserBody>("addUser");
    const editUserForm = getForm<IUserBody>("editUser");
    const changeUserPasswordForm = getForm<IUserBody>("changeUserPassword");
    const [rolesOptions, setRolesOptions] = useState<ICustomOption[]>([]);
    const [areasOptions, setAreasOptions] = useState<ICustomOption[]>([]);

    // pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<ReactNode>(<></>);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    const setActionsWithPermissions = (user: IUser) => {
        let tempActions: ({ type: ACTIONS, message: string, disabled?: boolean })[] = []
        if (user.hasPendingRegistration) {
            tempActions.push(
                {
                    type: ACTIONS.PENDING,
                    message: t("usersPage.pendingUserButtonLabel"),
                    disabled: false
                }
            )
            tempActions.push(
                {
                    type: ACTIONS.PASSWORD,
                    message: t("usersPage.pendingUserDisabledLabel"),
                    disabled: true
                }
            )
            if (permissionsState && permissionsState.existEditUsersPermission)
                tempActions.push({
                    type: ACTIONS.EDIT,
                    message: t("usersPage.pendingUserDisabledLabel"),
                    disabled: user.hasPendingRegistration
                })
            if (permissionsState && permissionsState.existDeleteUsersPermission)
                tempActions.push({
                    type: ACTIONS.DELETE,
                    message: user.hasManagedAreas ? t("usersPage.deleteUserButtonDisabledLabel") : t("usersPage.deleteUserButtonLabel"),
                    disabled: !user.hasPendingRegistration ? false : (user.hasManagedAreas ? user.hasManagedAreas : false)
                })
        } else {
            tempActions.push(
                {
                    type: ACTIONS.PASSWORD,
                    message: t("usersPage.changePasswordButtonLabel")
                }
            )
            if (permissionsState && permissionsState.existEditUsersPermission)
                tempActions.push(
                    {
                        type: ACTIONS.EDIT,
                        message: t("usersPage.editUserButtonLabel")
                    }
                )
            if (permissionsState && permissionsState.existDeleteUsersPermission)
                tempActions.push(
                    {
                        type: ACTIONS.DELETE,
                        message: user.hasManagedAreas ? t("usersPage.deleteUserButtonDisabledLabel") : t("usersPage.deleteUserButtonLabel"),
                        disabled: user.hasManagedAreas || false
                    }
                )
        }
        tempActions = tempActions.filter(action => action !== undefined)
        return tempActions
    }

    const getUsersList = useCallback(async () => {
        try {
            const response = await userService.getAll(page * rowsPerPage, rowsPerPage);
            let rows = response.content.map(user => {
                return {
                    ...user,
                    roleName: user.role ? user.role.name : "-",
                    areaOfExpertise: user.area ? user.area.name : "-",
                    actions: setActionsWithPermissions(user)
                }
            })
            setUsers(rows)
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }, [page, rowsPerPage])

    const getRolesOptionsList = async () => {
        try {
            const response = await rolesService.getAll(0, 1000);
            let options: ICustomOption[] = response.content.map(role => {
                return {
                    desc: role.name,
                    value: role.id
                }
            })
            setRolesOptions(options);
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    const getAreasOptionsList = async () => {
        try {
            const response = await areaExpertiseService.getAll(0, 1000);
            let options: ICustomOption[] = response.content.map(area => {
                return {
                    desc: area.name,
                    value: area.id
                }
            })
            setAreasOptions(options);
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    useEffect(() => {
        getUsersList();
    }, [getUsersList, page, rowsPerPage])

    // page change handler (pagination)
    const handleChangePage = (
        _event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage);
    };

    // num. of rows change handler (p                                                                                                                            agination)
    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value));
    };

    // action handler (icon buttons from the table)
    const onActionClicked = (label: ACTIONS, data: IUser & { actions: { type: ACTIONS, message: string }[] } | undefined) => {
        setAction(label);
        if (data) {
            const { actions, ...userData } = data
            setCurrentUser(userData);
        }
        switch (label) {
            case "add": {
                getRolesOptionsList();
                getAreasOptionsList();
                setDialogTitle(t("usersPage.addDialogTitle"));
                setDialogBody(undefined);
                setOpenDialog(true);
                break;
            }
            case "edit": {
                if (data) {
                    getRolesOptionsList();
                    getAreasOptionsList();
                    setDialogTitle(t("usersPage.editDialogTitle") + '"' + data.firstName + ' ' + data.lastName + '"');
                    setDialogBody(undefined);
                    setOpenDialog(true);
                }
                break;
            }
            case "change-password": {
                if (data) {
                    setDialogTitle(t("usersPage.changePasswordDialogTitle") + '"' + data.firstName + ' ' + data.lastName + '"');
                    setDialogBody(undefined);
                    setOpenDialog(true);
                }
                break;
            }
            case "delete": {
                if (data) {
                    setDialogTitle(t("usersPage.deleteDialogTitle") + '"' + data.firstName + ' ' + data.lastName + '"?'
                    );
                    setDialogBody(
                        <Typography variant="body1">
                            {t("usersPage.deleteDialogWarningText")}
                        </Typography>
                    );
                    setOpenDialog(true);
                }
                break;
            }
            case "pending": {
                if (data) {
                    setDialogTitle(t("usersPage.confirmSendActivationEmailTitle") + data.firstName + " " + data.lastName);
                    setDialogBody(
                        <Typography variant="body1">
                            {t("usersPage.confirmSendActivationEmailBody")}
                            <Typography sx={{ fontWeight: 600, mt: 1, textAlign: "center" }}>{data.emailAddress}</Typography>
                        </Typography>
                    );
                    setOpenDialog(true);
                }
                break;
            }
            default: {
                break;
            }
        }
    };

    // close dialog handler
    const handleClose = () => {
        setOpenDialog(false);
    };

    // api request handler
    const apiRequest = (data: IUserBody | string | undefined) => {
        if (data && typeof data !== "string") {
            if (action === "add" && data)
                return userService.post(data)
            if (action === "edit" && currentUser)
                return userService.put(currentUser.id, data)
            if (data && action === "change-password" && currentUser)
                return userService.changePassword(currentUser.id, data.password)
        } else if (action === "delete" && currentUser) {
            return userService.delete(currentUser.id)
        } else if (action === "pending" && currentUser) {
            return userService.sendActivationEmail(currentUser.id)
        } else {
            return Promise.reject()
        }
    }

    // confirm dialog handler
    const handleActionConfirm = async (data: IUserBody | undefined) => {
        handleClose();
        try {
            await apiRequest(data);
            setOpenSnackbar(true);
            setResponseType("success");
            setMessageSnackbar(t("genericSuccess"));
            await getUsersList();
        } catch (error: unknown) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.content.message)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    // pagination props
    const paginationProps: ICustomPaginationProps = {
        type: "table",
        paginationProps: {
            count: users.length,
            page,
            onPageChange: handleChangePage,
            rowsPerPage,
            onRowsPerPageChange: handleChangeRowsPerPage,
        },
    };

    // dialog props
    const dialogProps: ICustomDialogProps = {
        type: action === "delete" || action === "pending" ? "confirm" : "form",
        close: handleClose,
        confirm: () => handleActionConfirm(undefined),
        title: dialogTitle,
        open: openDialog,
        confirmButtonLabel: action === "delete" || action === "pending" ? t("usersPage.usersForm.confirmButton") : t("usersPage.usersForm.saveChangesButton"),
        closeButtonLabel: t("usersPage.usersForm.cancelButton"),
        fullWidth: true,
        formProps: action !== "change-password" ? (
            action === "add" ? {
                ...addUserForm,
                inputs: [
                    ...addUserForm.inputs.map(input => {
                        if (input.name === "roleId")
                            return { ...input, options: rolesOptions }
                        if (input.name === "areaId")
                            return { ...input, options: areasOptions }
                        else
                            return input
                    })
                ],
                initialValues: {
                    firstName: "",
                    lastName: "",
                    emailAddress: "",
                    password: "",
                    roleId: "",
                    areaId: ""
                },
                isDialog: true,
                submit: (data) => handleActionConfirm(data),
                customStyles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }
            } : {
                ...editUserForm,
                inputs: [
                    ...editUserForm.inputs.map(input => {
                        if (input.name === "roleId")
                            return { ...input, options: rolesOptions }
                        if (input.name === "areaId")
                            return { ...input, disabled: currentUser && currentUser.hasManagedAreas, options: areasOptions }
                        else
                            return input
                    })
                ],
                initialValues: {
                    ...currentUser,
                    areaId: currentUser && currentUser.area ? currentUser.area.id : "",
                    roleId: currentUser && currentUser.role ? currentUser.role.id : "",
                },
                isDialog: true,
                submit: (data) => handleActionConfirm(data),
                customStyles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }
            }
        ) : {
            ...changeUserPasswordForm,
            initialValues: {
                password: "",
                confirmPassword: ""
            },
            isDialog: true,
            submit: (data) => handleActionConfirm(data)
        }
    };

    return <Box sx={{ py: 1, px: 2, minHeight: "calc(100vh - 198px)" }}>
        <CustomDialog {...dialogProps}>{dialogBody}</CustomDialog>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 0 }}>
                {t("usersPage.title")}
            </Typography>
            {
                permissionsState &&
                permissionsState.existCreateUsersPermission &&
                <Button
                    sx={(theme) => ({
                        [theme.breakpoints.only("xs")]: {
                            minWidth: { xs: 0 },
                            width: { xs: 34 },
                            height: { xs: 32 },
                            padding: { xs: 2 }
                        }
                    })}
                    variant="contained"
                    onClick={() => onActionClicked(ACTIONS.ADD, undefined)}
                >
                    <Add sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                    {largeMediumScreen && t("usersPage.newUserButton")}
                </Button>
            }
        </Box>
        {
            loading && !openDialog ? <CustomLoader /> : <Box>
                <CustomTable
                    headcells={usersHeadcells}
                    rows={users}
                    onIconActionClicked={onActionClicked}
                    pagination={paginationProps}
                />
            </Box>
        }
    </Box>
}

export default Users;